import {
  InvoicePrintField,
  InvoicePrintModel,
  InvoicePrintParty,
  InvoicePrintTotals,
} from '../../../invoice-print/core/_models'
import {PrintColumn, resolveColumns} from '../../../invoice-print/core/printColumns'
import {sumNumbers} from '../../../invoice-print/core/printNumbers'
import {
  isProformaPrint,
  noteLines,
  totalRows,
  TotalRow,
} from '../../../invoice-print/core/printModel'
import {persianText} from '../../../invoice-print/core/printText'
import {UploadedFile} from '../../../../../../../_metronic/helpers/components/uploadTypes'
import {Invoice} from '../../core/_models'
import {isTaxGovApproved} from '../../core/invoiceStatusHelpers'

// What the earlier print page (/invoicePrint/{hash}) prints, taken from the same printed invoice as
// the A4 page (api/public/invoice-print/{hash}): one source for every value and every pattern rule
// (RC_IITP.IS_V7.9, printableFields). The earlier page keeps only its own arrangement. Pure, so it
// is tested without rendering.

export type ClassicField = Pick<InvoicePrintField, 'key' | 'title' | 'value' | 'format'>

/** «صورتحساب الکترونیکی نوع ۱ (با مشخصات کامل خریدار)»; a proforma is printed as «پیش‌فاکتور». */
export const classicTitle = (invoice: InvoicePrintModel) =>
  isProformaPrint(invoice) ? 'پیش‌فاکتور' : `صورتحساب الکترونیکی نوع ${invoice.invoiceTypeTitle}`

/** «الگوی ۱ (فروش)»; a proforma adds its validity and that it is no tax invoice. */
export const classicSubtitle = (invoice: InvoicePrintModel) =>
  isProformaPrint(invoice)
    ? `الگوی ${invoice.pattern} (${invoice.patternTitle})${
        invoice.proforma?.validUntil ? ` — معتبر تا ${invoice.proforma.validUntil}` : ''
      } — صورتحساب مالیاتی نیست`
    : `الگوی ${invoice.pattern} (${invoice.patternTitle})`

/** A replaced or cancelled invoice says so under its title, in red; so does an expired proforma. */
export const invalidityNote = (invoice: InvoicePrintModel): string | null =>
  isProformaPrint(invoice)
    ? invoice.proforma?.state === 'expired' || invoice.proforma?.state === 'converted'
      ? invoice.status.detail
      : null
    : invoice.status.code === 'superseded' || invoice.status.code === 'cancelled'
    ? invoice.status.title
    : null

/** The tax administration's emblem beside the QR code: only on an invoice the tax system registered. */
export const showsRegisteredEmblem = (invoice: InvoicePrintModel) =>
  invoice.status.code === 'registered' && invoice.status.isActive && !!invoice.taxId

/** The rows' columns: the A4 page's, so a column out of the invoice's pattern is never printed. */
export const classicColumns = (invoice: InvoicePrintModel): PrintColumn[] => resolveColumns(invoice)

/** The unit under a column's title: amounts are in rials, net weights in kilograms. */
export const columnUnit = (column: PrintColumn): string | null => {
  if (column.key === 'netWeight') return 'کیلوگرم'
  if (column.key === 'purchaseRate') return 'ریال'
  if (column.kind === 'amount' && column.key !== 'exchangeRate') return 'ریال'
  return null
}

/** The columns before the first summable one hold the totals' labels. */
export const totalsLabelSpan = (columns: PrintColumn[]) =>
  Math.max(
    1,
    columns.findIndex((column) => column.sum)
  )

/** The invoice's own total (as the tax system received it) of the column it belongs to. */
const HEADER_TOTAL_OF_COLUMN: Record<string, keyof InvoicePrintTotals> = {
  netWeight: 'netWeight',
  rialValue: 'rialValue',
  currencyValue: 'currencyValue',
  amountBeforeDiscount: 'amountBeforeDiscount',
  discount: 'discount',
  amountAfterDiscount: 'amountAfterDiscount',
  vatAmount: 'vat',
  total: 'total',
}

/**
 * «جمع کل» under every summable column: the invoice's own total when the column has one (tprdis,
 * tdis, tadis, tvam, tbill, tonw, torv, tocv), otherwise the exact sum of the rows (the gold fees,
 * other taxes, legal funds, VAT bases…).
 */
export const grandTotals = (
  invoice: InvoicePrintModel,
  columns: PrintColumn[]
): Record<string, string | null> => {
  const totals: Record<string, string | null> = {}
  columns
    .filter((column) => column.sum)
    .forEach((column) => {
      const field = HEADER_TOTAL_OF_COLUMN[column.key]
      const header = field ? invoice.totals[field] : null
      totals[column.key] = header ?? sumNumbers(invoice.items.map(column.value))
    })
  return totals
}

/** The header total each column's «جمع کل» already prints. */
const COLUMN_OF_TOTAL_ROW: Record<string, string> = {
  tprdis: 'amountBeforeDiscount',
  tdis: 'discount',
  tadis: 'amountAfterDiscount',
  tonw: 'netWeight',
  torv: 'rialValue',
  tocv: 'currencyValue',
  tvam: 'vatAmount',
  tbill: 'total',
}

/**
 * Header totals no column prints: other taxes and legal funds together (todam), the article 17
 * tax, or a total whose column this invoice does not show (currency values of several currencies).
 */
export const extraTotalRows = (invoice: InvoicePrintModel, columns: PrintColumn[]): TotalRow[] =>
  totalRows(invoice).filter((row) => {
    const columnKey = COLUMN_OF_TOTAL_ROW[row.key]
    return !columnKey || !columns.some((column) => column.key === columnKey && column.sum)
  })

/** A party's codes and contact details, each only when it has a value. */
export const partyFields = (party: InvoicePrintParty): ClassicField[] => {
  const fields: ClassicField[] = []
  if (party.personTypeTitle) {
    fields.push({key: 'personType', title: 'نوع شخص', value: party.personTypeTitle, format: 'text'})
  }
  party.identifiers.forEach((identifier) => fields.push(identifier))
  if (party.phone) fields.push({key: 'phone', title: 'تلفن', value: party.phone, format: 'code'})
  if (party.postalCode) {
    fields.push({key: 'postalCode', title: 'کد پستی', value: party.postalCode, format: 'code'})
  }
  return fields
}

/** How the passenger was identified (pattern 6), written next to the identifier it names. */
const PASSENGER_METHODS: Record<number, {title: string; field: string}> = {
  1: {title: 'شماره ملی', field: 'passengerNationalId'},
  2: {title: 'کد فراگیر اتباع غیرایرانی', field: 'passengerForeignId'},
  3: {title: 'گذرنامه', field: 'bpn'},
}

/**
 * The pattern's fields, then the notes (nti1, nti2). Pattern 6 keeps the earlier page's
 * «روش شناسایی مسافر», only when the passenger identifier it names is printed.
 */
export const classicPatternFields = (
  invoice: InvoicePrintModel,
  earlier?: Pick<Invoice, 'fltin'>
): ClassicField[] => {
  const fields: ClassicField[] = [...invoice.patternFields]
  const method = invoice.pattern === 6 ? PASSENGER_METHODS[earlier?.fltin?.buyerMethod || 0] : null
  const index = method ? fields.findIndex((field) => field.key === method.field) : -1
  if (method && index >= 0) {
    fields.splice(index, 0, {
      key: 'passengerMethod',
      title: 'روش شناسایی مسافر',
      value: method.title,
      format: 'text',
    })
  }
  invoice.notes.forEach((note) => fields.push({...note, value: persianText(note.value)}))
  return fields
}

export const hasPatternBlock = (fields: ClassicField[], invoice: InvoicePrintModel) =>
  fields.length > 0 || invoice.shippedGoods.length > 0

export type ClassicAttachment = {name: string; source: string}

export type ClassicNoteLine =
  | {kind: 'text'; text: string}
  | {kind: 'attachments'; text: string; files: ClassicAttachment[]}

const attachmentName = (file: UploadedFile) =>
  file.options?.file?.name || file.source.split('/').filter(Boolean).pop() || file.source

/**
 * The description and the template's text as plain lines (the server removes any markup), then the
 * invoice's attachments on a line of their own; split between sheets like rows.
 */
export const classicNoteLines = (
  invoice: InvoicePrintModel,
  files: UploadedFile[] | undefined
): ClassicNoteLine[] => {
  const lines: ClassicNoteLine[] = noteLines(invoice).map((text) => ({kind: 'text', text}))
  const attachments = (files || [])
    .filter((file) => file && typeof file.source === 'string' && file.source.trim())
    .map((file) => ({name: attachmentName(file), source: file.source}))
  if (attachments.length > 0) {
    lines.push({
      kind: 'attachments',
      text: `ضمائم: ${attachments.map((file) => file.name).join('، ')}`,
      files: attachments,
    })
  }
  return lines
}

/** The notes block's title: «توضیحات», or «ضمائم» when the invoice only has attachments. */
export const notesTitle = (lines: ClassicNoteLine[]) =>
  lines.some((line) => line.kind === 'text') ? 'توضیحات' : 'ضمائم'

const ZWNJ = '‌'

/**
 * What the earlier page's QR code holds, unchanged: the page's address when the panel is online,
 * otherwise a short text (the tax ID once registered, the total, the seller's and buyer's codes).
 */
export const classicQrValue = (
  invoice: Pick<Invoice, 'hsh' | 'ph' | 'tgs' | 'txi' | 'ntp' | 'slc' | 'buc'>,
  options: {online: boolean; url: string; project: string}
): string => {
  if (options.online) return `${options.url}/invoicePrint/${invoice.hsh}`
  const lines = [`سامانه ${options.project}`]
  if (invoice.ph?.i === 3 && isTaxGovApproved(invoice.tgs) && invoice.txi) lines.push(invoice.txi)
  lines.push(invoice.ntp ?? '')
  lines.push(`فروشنده: ${ZWNJ.repeat(3)}${invoice.slc ?? ''}${ZWNJ}`)
  if (invoice.buc) lines.push(`خریدار: ${ZWNJ}${invoice.buc}${ZWNJ}`)
  return lines.join('\n')
}
